import { Link } from 'react-router-dom'
import { REPORTS_LIST_PATH } from '@config/reportRoutes'
import { QUEUE_HANDOFF_MS } from '@config/queueConfig'
import { ClockIcon } from './queueIcons'

/**
 * Screen 8A / Himaad's `Queue / Empty` (589:9770) — the empty queue.
 *
 * TWO empties, and the designer's annotation is explicit that this is deliberate:
 * *"This is the first-run state. It is deliberately different from an empty tab,
 * which says nothing matches that filter rather than nothing exists."*
 *
 * There is a THIRD state that the design does not cover and that this component
 * must never be asked to render: a FAILED read. That one lives on the queue page
 * (`listError`), because "nothing exists" and "we could not find out" are
 * different claims about someone's financial data and this repo has conflated
 * them six times. If you are adding a caller here, the gate is `!error`.
 *
 * The first-run copy explains the auto-queue rule up front, so a user who has
 * never queued anything still understands why things will appear here later. The
 * threshold is read from `QUEUE_HANDOFF_MS`, never typed — it is env-overridable.
 */
const JobEmptyState = ({ onQueueReport, filtered = false, onClearFilter }) => {
  if (filtered) {
    return (
      <div className="px-5 py-16 text-center">
        {/* Says the filter is what is empty, not the queue — the annotation's
            whole point. "No jobs match this filter" would be a lie on a queue
            that has eight runs in it and none on this tab. */}
        <p className="text-sm text-gray-500">Nothing in the queue matches that filter.</p>
        <button type="button" onClick={onClearFilter} className="btn-cb-tertiary mt-2">
          Show everything in the queue
        </button>
      </div>
    )
  }

  return (
    <div className="px-5 py-12 text-center">
      <div className="mx-auto mb-3.5 flex h-14 w-14 items-center justify-center rounded-full bg-cb-light text-cb-accent-text">
        <ClockIcon className="w-6 h-6" />
      </div>
      <h4 className="mb-1.5 text-[14.5px] font-semibold text-cb-navy">Nothing in your queue yet</h4>
      <p className="mx-auto mb-3.5 max-w-[44ch] text-[12.5px] leading-relaxed text-gray-500">
        Queue a report to run in the background, or set one up to run on a schedule.
        Anything that takes longer than {Math.round(QUEUE_HANDOFF_MS / 1000)} seconds lands here on its own.
      </p>
      <div className="flex items-center justify-center gap-2">
        <button type="button" onClick={onQueueReport} className="btn-cb-primary">
          Queue a report
        </button>
        <Link to={REPORTS_LIST_PATH} className="btn-cb-tertiary">
          Browse reports →
        </Link>
      </div>
    </div>
  )
}

export default JobEmptyState
